// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// Create the Supabase client with more robust network settings
export const supabase = createClient<Database>(
  SUPABASE_URL, 
  SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: {
        getItem: (key) => {
          try {
            return localStorage.getItem(key);
          } catch (error) {
            console.error('Error reading from localStorage:', error);
            return null;
          }
        },
        setItem: (key, value) => {
          try {
            localStorage.setItem(key, value);
          } catch (error) {
            console.error('Error writing to localStorage:', error);
          }
        },
        removeItem: (key) => {
          try {
            localStorage.removeItem(key);
          } catch (error) {
            console.error('Error removing from localStorage:', error);
          }
        }
      }
    },
    global: {
      fetch: async (url, options) => {
        const maxRetries = 3;
        let retryCount = 0;
        
        while (retryCount < maxRetries) {
          try {
            const response = await fetch(url, options);
            // Return the response even if it's not OK
            // This allows Supabase to handle error codes properly
            return response;
          } catch (error) {
            retryCount++;
            if (retryCount === maxRetries) {
              console.error('Network error during Supabase request after retries:', error);
              throw error;
            }
            // Wait before retrying (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
          }
        }
      }
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  }
);

// Helper function to create a slug from a business name
export const createSlugFromName = (name: string) => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-{2,}/g, "-") // Replace multiple consecutive hyphens with a single one
    .replace(/^-+|-+$/g, ""); // Remove hyphens from beginning and end
};

// Helper function to generate a service page link
export const generateServiceLink = (userId: string) => {
  // Use only the userId without any business name reference
  return `services/${userId}`;
};

// Helper function to update payment status for service requests - simplified version
export const updateServiceRequestPaymentStatus = async (requestId: string, paid: boolean) => {
  try {
    console.log(`Client: Updating payment for request ${requestId} to ${paid}`);
    
    // Try up to 3 times if there's a network error
    let attempts = 0;
    let lastError = null;
    
    while (attempts < 3) {
      try {
        console.log(`Attempt ${attempts + 1} - Updating service request payment status:`, { 
          requestId,
          paid 
        });
        
        // Use the simpler RPC function now
        const { data, error } = await supabase.rpc(
          'service_payment_update',
          { 
            request_id: requestId, 
            is_paid: paid 
          }
        );
          
        if (error) {
          console.error('Client: Error updating payment status:', error);
          throw error;
        }
        
        console.log('Client: Payment update successful:', data);
        return { success: true, data };
      } catch (err) {
        lastError = err;
        attempts++;
        if (attempts < 3) {
          console.log(`Retrying (${attempts}/3)...`);
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
        }
      }
    }
    
    throw lastError; // Re-throw the last error if all attempts failed
  } catch (error) {
    console.error('Client: Exception updating payment status:', error);
    return { success: false, error };
  }
};

// Helper function to save social links for a user - optimized to use a single request
export const saveSocialLinks = async (userId: string, socialLinks: any[]) => {
  try {
    console.log('Saving social links in bulk for user:', userId);
    
    // First, fetch existing records to determine which need to be inserted vs updated
    const { data: existingLinks, error: fetchError } = await supabase
      .from('social_links')
      .select('id, platform_id')
      .eq('user_id', userId);
    
    if (fetchError) throw fetchError;
    
    const existingLinkMap = new Map();
    if (existingLinks) {
      existingLinks.forEach(link => {
        existingLinkMap.set(link.platform_id, link.id);
      });
    }

    // Prepare batches of updates and inserts
    const updatesArray = [];
    const insertsArray = [];

    for (const link of socialLinks) {
      const socialLinkData = {
        user_id: userId,
        platform_id: link.id,
        enabled: link.enabled,
        username: link.details.username || null,
        url: link.details.url || null,
        location: link.details.location || null,
        title: link.details.title || null,
      };

      if (existingLinkMap.has(link.id)) {
        // Add to updates batch with ID
        updatesArray.push({
          ...socialLinkData,
          id: existingLinkMap.get(link.id)
        });
      } else {
        // Add to inserts batch
        insertsArray.push(socialLinkData);
      }
    }

    // Process updates in a single batch if there are any
    if (updatesArray.length > 0) {
      const { error: updateError } = await supabase
        .from('social_links')
        .upsert(updatesArray, { onConflict: 'id' });
        
      if (updateError) throw updateError;
    }

    // Process inserts in a single batch if there are any
    if (insertsArray.length > 0) {
      const { error: insertError } = await supabase
        .from('social_links')
        .insert(insertsArray);
        
      if (insertError) throw insertError;
    }

    console.log('Successfully saved all social links in bulk');
    return { success: true };
  } catch (error) {
    console.error('Error saving social links in bulk:', error);
    return { success: false, error };
  }
};

// Helper function to save availability settings
export const saveAvailabilitySettings = async (userId: string, timeSlots: any[]) => {
  try {
    // First, fetch existing records to determine which need to be inserted vs updated
    const { data: existingSettings, error: fetchError } = await supabase
      .from('availability_settings')
      .select('id, day_of_week')
      .eq('user_id', userId);
    
    if (fetchError) throw fetchError;
    
    const existingSettingsMap = new Map();
    if (existingSettings) {
      existingSettings.forEach(setting => {
        existingSettingsMap.set(setting.day_of_week, setting.id);
      });
    }

    // Process each time slot - update existing ones
    for (const slot of timeSlots) {
      const settingData = {
        user_id: userId,
        day_of_week: slot.dayOfWeek,
        start_time: slot.startTime,
        end_time: slot.endTime,
        is_available: slot.isAvailable
      };

      if (existingSettingsMap.has(slot.dayOfWeek)) {
        // Update existing record
        const { error } = await supabase
          .from('availability_settings')
          .update(settingData)
          .eq('id', existingSettingsMap.get(slot.dayOfWeek));
          
        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from('availability_settings')
          .insert(settingData);
          
        if (error) throw error;
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Error saving availability settings:', error);
    return { success: false, error };
  }
};

// Utility function to check connection health
export const checkSupabaseConnection = async () => {
  try {
    // Simple health check by getting the server timestamp
    const startTime = Date.now();
    // Use a simple and valid RPC function or a direct query instead
    const { data, error } = await supabase
      .from('profiles')
      .select('count(*)', { count: 'exact', head: true });
    
    if (error) {
      console.error('Supabase connection error:', error);
      return { connected: false, error };
    }
    
    const endTime = Date.now();
    const responseTime = endTime - startTime;
    
    console.log('Supabase connection healthy, response time:', responseTime + 'ms');
    return { connected: true, responseTime: responseTime + 'ms' };
  } catch (error) {
    console.error('Failed to check Supabase connection:', error);
    return { connected: false, error };
  }
};
